import React from "react";
import Question from "../Question";
import Intro from "../Intro";
import Profile from "../Profile";
import StateNextStep from "../StateNextStep";
import RedirectFirstQuestion from "../RedirectFirstQuestion";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { saveDataToDb } from "../../utils/saveDataToDb";
import useFocusById from "../../utils/useFocusById";
import {
  isAtFirstQuestion,
  getNextQuestion,
  getAnswerForCurrentQuestion,
  getAnswerIndexForCurrentQuestion,
  saveAppState,
  mergeAppState
} from "../../utils/branching";
import routes from "../../routes";
import useAppContext from "../AppContext";
import useUrlSlugContext from "../UrlSlugContext";
import { PHQ9B, SUICIDE1A } from "../../config/tags";
import { STATE_NEXT_STEP_PAGE } from "../Profile/constants";
import { commonRouting } from "./routing/commonRouting";
import {
  uni1Routing,
  uni2Routing,
  uni3Routing,
  uni4Routing,
  uniRouting
} from "./routing/uniRouting";
import { sequenceRouting } from "./routing/sequenceRouting";
import { coachRouting } from "./routing/coachRouting";

export default function Assessment() {
  const { baseUrl, queryString } = useUrlSlugContext();
  const {
    assessment,
    answers,
    setAnswers,
    profile,
    contact,
    nextStack,
    setNextStack,
    newQuiz,
    authData,
    setProfile
  } = useAppContext();
  const { questionPath } = useParams();

  const focus = useFocusById();
  const history = useHistory();
  const location = useLocation();

  const nextQuestion = getNextQuestion(
    nextStack,
    questionPath,
    assessment,
    answers,
    true,
    true
  );

  const question = assessment?.questions?.find(q => q.urlPath === questionPath);

  const answerForCurrentQuestion = getAnswerForCurrentQuestion(
    answers,
    question?._id
  );

  if (!assessment) {
    return null;
  }

  if (!question) {
    return <RedirectFirstQuestion />;
  }

  if (
    !isAtFirstQuestion(questionPath, assessment.questions) &&
    !answers?.length
  ) {
    return <RedirectFirstQuestion />;
  }

  const pushToNextStack = answer => {
    const newNextStack = [...nextStack];
    newNextStack.push({
      returnQuestionId: answer.answers[0].returnQuestionId
    });
    setNextStack(newNextStack);
  };

  const popFromNextStack = () => {
    if (nextStack.length > 0) {
      const newNextStack = [...nextStack];
      const topStack = newNextStack.pop();
      setNextStack(newNextStack);
      return topStack;
    }

    return null;
  };

  const proceed = () => {
    focus("appTop");

    saveAppState(
      baseUrl,
      location,
      answers,
      profile,
      contact,
      nextStack,
      queryString
    );

    // We have to get next question here again
    // since on e.g. on intro page there is no click on answer,
    // just next button which sets state with new answer and
    // immediately calls proceed() to get to the next question.
    const nextQuestion = getNextQuestion(
      nextStack,
      questionPath,
      assessment,
      answers,
      true,
      false
    );

    let nextUrlPath;

    if (nextQuestion) {
      if (answerForCurrentQuestion?.answers[0]?.returnQuestionId) {
        pushToNextStack(answerForCurrentQuestion);
      }
      nextUrlPath = routes.question(nextQuestion.urlPath);

      if (!newQuiz.eli && !newQuiz.isSequence) {
        nextUrlPath = coachRouting({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          newQuiz
        });
      }

      if (newQuiz.eli || newQuiz.isSequence || newQuiz.isUni) {
        nextUrlPath = commonRouting({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile
        });
      }

      if (newQuiz.isUni) {
        nextUrlPath = uniRouting({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile
        });
      }

      if (newQuiz.isUni1) {
        nextUrlPath = uni1Routing({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile,
          newQuiz
        });
      }

      if (newQuiz.isUni2) {
        nextUrlPath = uni2Routing({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile
        });
      }

      if (newQuiz.isUni3) {
        nextUrlPath = uni3Routing({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile
        });
      }

      if (newQuiz.isUni4) {
        nextUrlPath = uni4Routing({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile
        });
      }

      if (newQuiz.isSequence) {
        nextUrlPath = sequenceRouting({
          nextUrlPath,
          question,
          answers,
          assessment,
          routes,
          profile
        });
      }

      // Skip account creation if already logged in
      if (nextQuestion.isAccountCreation && authData?.loggedInAs) {
        nextUrlPath = routes.state;
      }
    } else {
      const returnQuestionInfo = popFromNextStack();

      if (returnQuestionInfo) {
        nextUrlPath = routes.question(
          assessment.questions.find(
            q => q._id === returnQuestionInfo.returnQuestionId
          )["urlPath"]
        );
      } else {
        nextUrlPath = routes.results;

        if (!newQuiz.eli && !newQuiz.isSequence) {
          nextUrlPath = coachRouting({
            nextUrlPath,
            question,
            answers,
            assessment,
            routes,
            newQuiz
          });
        }

        if (newQuiz.isUni3) {
          nextUrlPath = uni3Routing({
            nextUrlPath,
            question,
            answers,
            assessment,
            routes,
            profile
          });
        }

        if (newQuiz.isUni4) {
          nextUrlPath = uni4Routing({
            nextUrlPath,
            question,
            answers,
            assessment,
            routes,
            profile
          });
        }
      }
    }

    if (nextUrlPath !== routes.results) {
      saveDataToDb(newQuiz, answers, assessment);
    }

    if (
      !nextQuestion &&
      (newQuiz.isSequence || newQuiz.isUni3 || newQuiz.isUni4) &&
      (question.tags.includes(PHQ9B) || question.tags.includes(SUICIDE1A))
    ) {
      history.go(-1);
    } else {
      history.push(nextUrlPath);
    }
  };

  const goBack = () => {
    history.go(-1);
  };

  const handleAnswer = (newAnswers, profile, email = false) => {
    const { _id: questionId, variantId } = question;
    const newAnswerObj = {
      questionId,
      variantId,
      ...(profile ? { profile } : {}),
      ...(email ? { email } : {}),
      answers: newAnswers
    };

    const newAnswerIndex = getAnswerIndexForCurrentQuestion(
      answers,
      questionId
    );

    if (newAnswerIndex === -1) {
      answers.push(newAnswerObj);
    } else {
      answers[newAnswerIndex] = newAnswerObj;
    }

    const updatedAnswers = [...answers];
    setAnswers(updatedAnswers);
    mergeAppState({ answers: updatedAnswers }, location, baseUrl);
  };

  const handleIntro = () => {
    handleAnswer([question.answers.presentationMetadata[0]]);
    proceed();
  };

  const handleProfile = profile => {
    handleAnswer([question.answers.presentationMetadata[0]], profile);
    proceed();
  };

  switch (true) {
    case question?.isIntro:
      return (
        <Intro
          question={question}
          nextQuestion={nextQuestion}
          onNext={handleIntro}
          profile={profile}
        />
      );

    case (newQuiz.isSequence || newQuiz.isUni3 || newQuiz.isUni4) &&
      !!profile?.stateNextStepPage:
      return (
        <StateNextStep
          page={profile?.stateNextStepPage}
          proceed={() => {
            const updatedProfile = { ...profile, [STATE_NEXT_STEP_PAGE]: "" };
            setProfile(updatedProfile);
            mergeAppState({ profile: updatedProfile }, location, baseUrl);
            history.push(routes.medicalHistory);
          }}
        />
      );

    case question?.isProfile: {
      return (
        <Profile
          profile={profile}
          profileType={question.isProfile1 ? 1 : question.isProfile2 ? 2 : null}
          onNext={handleProfile}
          onBack={goBack}
          question={question}
          nextQuestion={nextQuestion}
        />
      );
    }

    default:
      return (
        <Question
          profile={profile}
          question={question}
          nextQuestion={nextQuestion}
          answer={answerForCurrentQuestion}
          handleAnswer={handleAnswer}
          onNext={proceed}
          onBack={goBack}
        />
      );
  }
}
